import type { Route } from "@route-types/Author/+types/Author";
import ThickChinaAvatarIcon from "@src/app/assets/author-default-thumbnail.jpg";
import ArticleAnnotation from "@src/app/components/Article/body/components/ArticleAnnotation";
import PageArticleList from "@src/app/components/ArticleList/PageArticleList";
import SectionContainer from "@src/app/components/SectionContainer/SectionContainer";
import {
  parsePayload,
  useRouteResponse,
} from "@src/app/hooks/useRouteResponse";
import type { AuthorPageResponse } from "@src/app/types/response";
import { createMetaTags } from "@src/app/utils/createMetaTags";
import { capitalizeWords } from "@src/app/utils/tools";

import { fetchAuthorPageData } from "./Author.server";

export const loader = fetchAuthorPageData;

export const meta = (args: Route.MetaArgs) => {
  const payload = parsePayload<AuthorPageResponse>(args.loaderData);
  const author = payload.author;

  return createMetaTags({
    title: author?.name
      ? `${author.name}'s Column | Big Reads, Opinions & Columns on China | ThinkChina`
      : "",
    description: author?.bio_raw || "",
    canonical: `https://www.thinkchina.sg/contributors/${args.params.contributor || ""}`,
    ogInfo: {
      ogTitle: `${author?.name}'s Column | Big Reads, Opinions & Columns on China`,
    },
  });
};

export default function AuthorPage() {
  const route = useRouteResponse("authorPage");
  const author = route.author;
  const authorTitle = capitalizeWords(author?.name || "");

  return (
    <div>
      {author ? (
        <SectionContainer className="p-4" showHeader={false}>
          <div
            data-testid={`contributor-card-${author.id}`}
            className="bg-background-secondary grid grid-cols-12 gap-y-3 px-3 py-4"
          >
            <div className="col-span-12 flex items-start gap-3 lg:col-span-6 lg:px-3 lg:py-4">
              <img
                src={author.imageUrl || ThickChinaAvatarIcon}
                alt={author.name}
                className="aspect-square size-20 rounded-full"
              />

              <div>
                <h4 className={"text-header-4 text-primary font-bold"}>
                  {author.name}
                </h4>
                {author.designation && (
                  <span
                    className={"text-label-2 text-tertiary block font-bold"}
                  >
                    {author.designation}
                  </span>
                )}
              </div>
            </div>

            <div className="text-body-2 text-secondary col-span-12 lg:col-span-6 lg:px-3 lg:py-4">
              {author.bio?.length ? (
                <ArticleAnnotation
                  value={author.bio[0]?.value}
                  annotations={author.bio[0]?.annotations}
                />
              ) : (
                <span>{author.bio_raw}</span>
              )}
            </div>
          </div>
        </SectionContainer>
      ) : null}

      <PageArticleList
        title={authorTitle ? `Articles by ${authorTitle}` : ""}
        initialData={route.articles || []}
        requestPath={`/contributors/${route.author?.id || ""}`}
      />
    </div>
  );
}
